import { useEffect, useMemo, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import { Calendar } from "@volca/ui/components/ui/calendar";
import { Popover, PopoverContent } from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { dayjs, formatDateRange } from "@/core/utils/date";
import type { IcmPeriod } from "@/icm/types";
import { IcmPeriodList } from "./icm-period-list";
import { IcmPeriodPickerTrigger } from "./icm-period-picker-trigger";

interface IcmPeriodPickerProps {
  periods: IcmPeriod[];
  calendarPeriods: IcmPeriod[];
  selectedPeriodStart: string | undefined;
  onSelect: (periodStart: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "default" | "lg";
  variant?: "field" | "button";
}

const parseLocalDate = (dateStr: string): Date => {
  const d = dayjs(dateStr);
  return d.startOf("day").toDate();
};

const PHONE_QUERY = "(max-width: 767px)";

const HOVER_MODIFIER_CLASS_NAMES = {
  hovered: "bg-neutral-subtle-hovered",
  hoveredStart: "rounded-s-md",
  hoveredEnd: "rounded-e-md",
};

const CALENDAR_END_MONTH = dayjs().add(12, "year").endOf("year").toDate();

export const IcmPeriodPicker = ({
  periods,
  calendarPeriods,
  selectedPeriodStart,
  onSelect,
  isLoading,
  disabled,
  className,
  size = "default",
  variant = "field",
}: IcmPeriodPickerProps) => {
  const [open, setOpen] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [month, setMonth] = useState<Date>(dayjs().toDate());
  const selectedItemRef = useRef<HTMLButtonElement>(null);

  const selectedPeriod = periods.find(
    (p) => p.period_start === selectedPeriodStart,
  );
  const selectedIdx = selectedPeriod ? periods.indexOf(selectedPeriod) : -1;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      const target = selectedPeriod ?? (periods.length > 0 ? periods[0] : null);
      const targetDate = target
        ? parseLocalDate(target.period_start)
        : dayjs().toDate();
      setMonth(
        window.matchMedia?.(PHONE_QUERY).matches
          ? targetDate
          : dayjs(targetDate).subtract(1, "month").toDate(),
      );
    }
    setHoveredIdx(null);
    setOpen(nextOpen);
  };

  // Narrow calendarPeriods to the two visible months ± 1 month buffer so
  // react-day-picker only evaluates ~14-18 modifier ranges on each hover render.
  // Anchors to `month` (the left panel) rather than the year to correctly
  // handle December/January cross-year views.
  const visibleCalendarPeriods = useMemo(() => {
    const rangeStart = dayjs(month).subtract(1, "month");
    const rangeEnd = dayjs(month).add(2, "months");
    return calendarPeriods.filter(
      (p) =>
        !dayjs(p.period_end).isBefore(rangeStart) &&
        !dayjs(p.period_start).isAfter(rangeEnd),
    );
  }, [calendarPeriods, month]);

  // O(1) date→period-index map built from the visible slice only.
  const dateToCalendarIdx = useMemo(() => {
    const map = new Map<string, number>();
    visibleCalendarPeriods.forEach((p, i) => {
      let d = dayjs(p.period_start);
      const end = dayjs(p.period_end);
      while (!d.isAfter(end)) {
        map.set(d.format("YYYY-MM-DD"), i);
        d = d.add(1, "day");
      }
    });
    return map;
  }, [visibleCalendarPeriods]);

  // Scroll the selected period into view when the popover opens
  useEffect(() => {
    if (open && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [open]);

  // Clear hover when the visible period slice changes (year navigation)
  useEffect(() => {
    setHoveredIdx(null);
  }, [visibleCalendarPeriods]);

  const selectedRange: DateRange | undefined = selectedPeriod
    ? {
        from: parseLocalDate(selectedPeriod.period_start),
        to: parseLocalDate(selectedPeriod.period_end),
      }
    : undefined;

  const hoveredPeriod =
    hoveredIdx !== null ? visibleCalendarPeriods[hoveredIdx] : null;
  const hoverModifiers =
    hoveredPeriod && hoveredPeriod.period_start !== selectedPeriodStart
      ? {
          hovered: {
            from: parseLocalDate(hoveredPeriod.period_start),
            to: parseLocalDate(hoveredPeriod.period_end),
          },
          hoveredStart: parseLocalDate(hoveredPeriod.period_start),
          hoveredEnd: parseLocalDate(hoveredPeriod.period_end),
        }
      : undefined;

  const handleDayMouseEnter = (day: Date) => {
    const key = dayjs(day).format("YYYY-MM-DD");
    const idx = dateToCalendarIdx.get(key) ?? -1;
    setHoveredIdx(idx >= 0 ? idx : null);
  };

  const handleDayClick = (day: Date) => {
    const key = dayjs(day).format("YYYY-MM-DD");
    const idx = dateToCalendarIdx.get(key) ?? -1;
    if (idx >= 0) {
      onSelect(visibleCalendarPeriods[idx].period_start);
      setOpen(false);
    }
  };

  const handleListSelect = (period: IcmPeriod) => {
    onSelect(period.period_start);
    setOpen(false);
  };

  const triggerText = selectedPeriod
    ? formatDateRange(selectedPeriod.period_start, selectedPeriod.period_end)
    : null;

  const isDisabled = disabled || isLoading;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <IcmPeriodPickerTrigger
        text={triggerText}
        isOpen={open}
        disabled={isDisabled}
        variant={variant}
        size={size}
        className={className}
      />

      <PopoverContent
        className="max-h-(--available-height) w-auto overflow-hidden p-0"
        align="end"
        sideOffset={8}
      >
        <Stack
          align="stretch"
          gap={0}
          className="max-h-(--available-height) md:flex-row"
        >
          {/* Beside the calendar, the list takes the calendar's height and
              scrolls; above it on a phone, it gets a height of its own. */}
          <Stack
            align="stretch"
            gap={0}
            className="relative shrink-0 border-b md:w-80 md:border-r md:border-b-0"
          >
            <IcmPeriodList
              periods={periods}
              selectedIdx={selectedIdx}
              selectedItemRef={selectedItemRef}
              onSelect={handleListSelect}
              className="max-h-48 md:absolute md:inset-0 md:max-h-none"
            />
          </Stack>

          <Stack
            align="stretch"
            gap={0}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            <Calendar
              mode="range"
              numberOfMonths={2}
              className="max-md:[&_.rdp-month+.rdp-month]:hidden"
              month={month}
              onMonthChange={setMonth}
              selected={selectedRange}
              onSelect={() => {}}
              showOutsideDays={false}
              captionLayout="dropdown"
              endMonth={CALENDAR_END_MONTH}
              modifiers={hoverModifiers}
              modifiersClassNames={HOVER_MODIFIER_CLASS_NAMES}
              onDayMouseEnter={handleDayMouseEnter}
              onDayClick={handleDayClick}
            />
            <Stack
              gap={4}
              direction="row"
              align="center"
              className="h-9 border-t px-3"
            >
              <Text weight="semibold" variant="caption" tone="muted">
                {hoveredPeriod
                  ? formatDateRange(
                      hoveredPeriod.period_start,
                      hoveredPeriod.period_end,
                    )
                  : selectedPeriod
                    ? formatDateRange(
                        selectedPeriod.period_start,
                        selectedPeriod.period_end,
                      )
                    : "Choose a date to pick its period"}
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
